<template>
    <v-app>
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel">
                        <v-expansion-panel>
                            <v-expansion-panel-title
                                color="blue-grey-lighten-4"
                                class="panel-header"
                            >
                                Debit Note Voucher List
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field
                                            v-model="search"
                                            append-inner-icon="mdi-magnify"
                                            label="Search"
                                            single-line
                                            hide-details
                                            density="compact"
                                            variant="outlined"
                                            bg-color="grey-lighten-4"
                                            class="pl-2"
                                            clearable
                                        ></v-text-field>
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group
                                                variant="outlined"
                                                density="compact"
                                                size="x-small"
                                                divided
                                            >
                                                <v-btn
                                                    prepend-icon="mdi-download"
                                                    variant="flat"
                                                    color="success"
                                                    @click="exportToCSV"
                                                >
                                                    CSV
                                                </v-btn>
                                                <v-btn
                                                    prepend-icon="mdi-download"
                                                    variant="flat"
                                                    color="primary"
                                                    @click="exportToExcel"
                                                >
                                                    EXCEL
                                                </v-btn>
                                                <v-btn
                                                    prepend-icon="mdi-download"
                                                    variant="flat"
                                                    color="red"
                                                    @click="exportToPDF"
                                                >
                                                    PDF
                                                </v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>

                                    <v-col md="3" cols="12">
                                        <div class="text-right">
                                            <v-btn
                                                v-if="$insertAccess($route.fullPath)"
                                                @click="goTo('/account/add-debit-note-voucher')"
                                                color="primary"
                                                variant="tonal"
                                                prepend-icon="mdi-plus"
                                                rounded="xl"
                                            >
                                                Debit Note  Voucher
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-data-table
                                    :headers="headers"
                                    :items="getSchema"
                                    :loading="loading"
                                    v-model:items-per-page="itemsPerPage"
                                    :search="search"
                                    :hover="true"
                                    density="compact"
                                    class="elevation-1"
                                >
                                    <template v-slot:item.index="{ index }">
                                        {{ index + 1 }}
                                    </template>

                                    <template v-slot:item.voucher_date="{ item }">
                                        {{ item.voucher_date }}
                                    </template>

                                    <template v-slot:item.total_debit="{ item }">
                                        {{ formatCurrency(item.total_debit) }}
                                    </template>

                                    <template v-slot:item.total_credit="{ item }">
                                        {{ formatCurrency(item.total_credit) }}
                                    </template>

                                    <template v-slot:item.approval_status_display="{ item }">
                                        <v-chip
                                        v-if="item.approval_current_process && item.approval_current_process.status === 'Pending'"
                                        color="yellow-darken-3"
                                        size="small"
                                        >
                                            Wating for {{ item.approval_current_process.approval_step_name.name }}
                                        </v-chip>

                                        <v-chip
                                        v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                                        color="green"
                                        size="small"
                                        >
                                            Approved By {{ item.approval_current_process.approval_step_name.name }}
                                        </v-chip>

                                        <v-chip
                                        v-else-if="item.approval_current_process && item.approval_current_process.status === 'Cancelled'"
                                        color="red"
                                        size="small"
                                        >
                                            Cancelled By  {{ item.approval_current_process.approval_step_name.name }}
                                        </v-chip>

                                        <v-chip
                                        v-else
                                        color="green"
                                        size="small"
                                        >
                                            {{ item.approval_status }}
                                        </v-chip>
                                    </template>

                                    <template v-slot:item.status="{ item }">
                                        <v-chip
                                            :color="statusMap[item.status]?.color || 'grey'"
                                            size="small"
                                            class="text-capitalize"
                                        >
                                            {{ statusMap[item.status]?.label || "Unknown" }}
                                        </v-chip>
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-icon
                                            small
                                            @click="viewVoucher(item)"
                                            class="me-2"
                                            title="View Voucher"
                                            color="blue"
                                        >
                                            mdi-file-document-outline
                                        </v-icon>
                                      
                                        <v-icon
                                            class="me-2 edit-button"
                                            color="primary"
                                            @click="editItem(item)"
                                            v-if="$updateAccess($route.fullPath)"
                                            title="Edit"
                                        >
                                            mdi-pencil-outline
                                        </v-icon>
                                    </template>
                                </v-data-table>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";
import statusMap from "@/utils/statusMap";

export default {
    name: 'DebitNoteVoucherList',
    
    data: () => ({
        itemsPerPage: 10,
        panel: [0],
        loading: false,
        search: "",
        statusMap,
        headers: [
            { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
            { title: "Code", key: "code", align: "center", sortable: false },
            { title: "Voucher Date", key: "voucher_date", align: "center", sortable: false },
            { title: "Party", key: "party_display", align: "center", sortable: false },
            { title: "Total Debit", key: "total_debit", align: "right", sortable: false },
            { title: "Total Credit", key: "total_credit", align: "right", sortable: false },
            { title: "Narration", key: "remarks", align: "left", sortable: false },
            {title: "Approval Status",align: "center", key: "approval_status_display",sortable: false,},
            { title: "Status", align: "center", key: "status", sortable: true },
            { title: "Actions", align: "center", key: "actions", sortable: false }
        ],
    }),

    created() {
        if (this.$store.getters.getVoucherList.length === 0) {
            this.loading = true;
            this.$store.dispatch('getVoucherList', { type: 'debit-note-voucher' })
                .finally(() => {
                    this.loading = false;
                });
        }
    },

    computed: {
        getSchema() {
            const vouchers = this.$store.getters.getVoucherList || [];
            const voucherDataList = vouchers.map(v => ({
                ...v,
                party_display: `${v.party?.party_name || ''} - ${v.party?.code || ''} - (${v.party?.type || ''})`
            }));
            return voucherDataList;
        },
    },

    setup() {
        const { handleSubmit, resetForm } = useForm({
            validationSchema: yup.object({
                from_date: yup.date().nullable().label('From Date'),
                to_date: yup.date().nullable().label('To Date'),
            }),
        });

        const from_date = useField("from_date");
        const to_date = useField("to_date");
        const errorMessages = ref("");
        const loading = ref(false);
        const store = useStore();

        const submit = handleSubmit((values) => {
            loading.value = true;
            const params = {
                type: 'debit-note-voucher',
                from_date: values.from_date ? formattedDate(from_date.value.value) : null,
                to_date: values.to_date ? formattedDate(to_date.value.value) : null,
            };

            store.dispatch("getVoucherList", params)
                .then((response) => {
                    loading.value = false;
                    toast.success('Data loaded successfully');
                })
                .catch((error) => {
                    loading.value = false;
                    toast.error(error.response?.data?.message || 'Failed to load data');
                });
        });

        return {
            submit,
            from_date,
            to_date,
            errorMessages,
            loading,
        };
    },

    methods: {
        goTo(val) {
            this.$router.push({ path: val }).catch((err) => {});
        },

        editItem(item) {
          this.$store.commit('setEditingVoucher', item);
            this.$router.push({
                name: "add-debit-note-voucher",
                query: { id: item.id },
            });
        },

        viewVoucher(item) {
            this.$router.push({
                name: "view-debit-note-voucher",
                params: { id: item.id },
            });
        },

        formatCurrency(value) {
            return parseFloat(value || 0).toFixed(2);
        },

        exportToPDF() {
            let serialNumber = 1;
            const columns = [
                "#SL",
                "Voucher No",
                "Voucher Date",
                "Total Debit",
                "Total Credit",
                "Narration",
                "Status",
            ];
            const rows = this.getSchema.map((item) => [
                serialNumber++,
                item.code || '-',
                item.voucher_date,
                this.formatCurrency(item.total_debit),
                this.formatCurrency(item.total_credit),
                item.remarks || '-',
                this.statusMap[item.status]?.label || 'Unknown',
            ]);
            this.$exportTableToPDF(
                columns,
                rows,
                "Debit Note Voucher List",
                "debit_note_voucher_list_" + this.$fileDate(),
                "landscape"
            );
        },

        exportToExcel() {
            var data = [
                [
                    "#SL",
                    "Voucher No",
                    "Voucher Date",
                    "Total Debit",
                    "Total Credit",
                    "Narration",
                    "Status",
                ],
            ];
            var serialNumber = 1;
            this.getSchema.forEach((item) => {
                data.push([
                    serialNumber++,
                    item.code || '-',
                    item.voucher_date,
                    this.formatCurrency(item.total_debit),
                    this.formatCurrency(item.total_credit),
                    item.remarks || '-',
                    this.statusMap[item.status]?.label || 'Unknown',
                ]);
            });
            this.$exportTableToExcel(data, "debit_note_voucher_list_" + this.$fileDate());
        },

        exportToCSV() {
            var data = [
                [
                    "#SL",
                    "Voucher No",
                    "Voucher Date",
                    "Total Debit",
                    "Total Credit",
                    "Narration",
                    "Status",
                ],
            ];
            var serialNumber = 1;
            this.getSchema.forEach((item) => {
                data.push([
                    serialNumber++,
                    item.code || '-',
                    item.voucher_date,
                    this.formatCurrency(item.total_debit),
                    this.formatCurrency(item.total_credit),
                    item.remarks || '-',
                    this.statusMap[item.status]?.label || 'Unknown',
                ]);
            });

            this.$exportTableToCSV(data, "debit_voucher_list_" + this.$fileDate());
        },
    },
    beforeUnmount() {
    this.$store.commit("UNSET_VOUCHER_LIST");
  },
};
</script>

<style scoped>
.edit-button {
    cursor: pointer;
}

.edit-button:hover {
    opacity: 0.7;
}
</style>